// ProcessDiffView — the process diff surface (plan
// 2026-07-07-process-scaffold-bpmn-diff P4). Left: changed-process picker fed
// by the ChangeSet. Right: the selected process's UNION scaffold — both sides
// walked, scaffolded and matched (diffScaffolds), laid out ONCE through
// bpmnLayout — rendered by BpmnView with diff accents (added = green,
// removed = red ghost, changed = amber + detail chip). Scrubber/step tooling
// stays off in diff mode: union stepIndexes are synthetic.

import { useMemo, useState } from "react";
import {
  buildScaffold,
  bpmnLayout,
  diffScaffolds,
  moduleNameIndex,
  walkProcess,
  walkerContextFor,
  swimlaneContextFor,
  estimateProcess,
  buildLatencyEnv,
  formatLatencyDelta,
  type BpmnLayout,
  type ChangeSet,
  type DiffKeyContext,
  type ProcessDiff,
  type ProcessScaffold,
  type ResolvedModel,
  type StableId,
} from "@archlang/engine";
import { useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { BpmnView } from "../process/BpmnView.js";

export interface ProcessDiffViewProps {
  readonly changeSet: ChangeSet;
  readonly beforeRaw: ResolvedModel;
  readonly afterRaw: ResolvedModel;
}

const EMPTY_SCAFFOLD: ProcessScaffold = {
  tree: [],
  linear: [],
  byIndex: new Map(),
  collapsedGroups: new Map(),
  parallelGroups: new Map(),
  reentries: [],
  compensation: [],
  triggers: [],
  diagnostics: [],
};

function scaffoldFor(model: ResolvedModel, id: StableId): ProcessScaffold | null {
  const proc = (model.processModels ?? []).find((p) => p.id === id);
  if (!proc) return null;
  const walk = walkProcess(
    proc,
    { branchSelection: new Map(), expandedSubprocesses: new Set() },
    walkerContextFor(model, proc),
    { showAllBranches: true },
  );
  return buildScaffold(walk, swimlaneContextFor(model));
}

function callableIndex(model: ResolvedModel): Map<string, StableId> {
  const out = new Map<string, StableId>();
  for (const s of model.subprocessModels ?? []) if (s.id) out.set(s.name, s.id);
  for (const p of model.processModels ?? []) if (p.id && p.name && !p.anonymous) out.set(p.name, p.id);
  return out;
}

const STATUS_CHIP: Record<string, string> = {
  added: "bg-success/[0.14] text-success border-success/30",
  removed: "bg-danger/[0.14] text-danger border-danger/30",
  modified: "bg-warning/[0.14] text-warning border-warning/30",
  renamed: "bg-sky-400/[0.14] text-sky-300 border-sky-400/30",
};

export function ProcessDiffView({ changeSet, beforeRaw, afterRaw }: ProcessDiffViewProps) {
  const menu = useContextMenu();
  const changed = useMemo(
    () =>
      [...changeSet.processes.values()]
        .filter((p) => p.status !== "unchanged" && p.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [changeSet],
  );
  const [selectedId, setSelectedId] = useState<StableId | null>(changed[0]?.id ?? null);
  // Compare sources can swap under a live pane — never leave the selection
  // pointing at a process absent from the new ChangeSet.
  const effectiveId = useMemo(
    () => (selectedId && changed.some((p) => p.id === selectedId) ? selectedId : changed[0]?.id ?? null),
    [selectedId, changed],
  );

  // v0.10.2 latency Δ — the selected process's base→head worst-case delta.
  const deltaLabel = useMemo(() => {
    if (!effectiveId) return null;
    const estOf = (model: ResolvedModel) => {
      const p = (model.processModels ?? []).find((x) => x.id === effectiveId);
      if (!p) return null;
      try {
        return estimateProcess(p, buildLatencyEnv(model, p));
      } catch {
        return null;
      }
    };
    return formatLatencyDelta(estOf(beforeRaw), estOf(afterRaw));
  }, [effectiveId, beforeRaw, afterRaw]);

  const layout: BpmnLayout | null = useMemo(() => {
    if (!effectiveId) return null;
    const b = scaffoldFor(beforeRaw, effectiveId);
    const a = scaffoldFor(afterRaw, effectiveId);
    if (!b && !a) return null;
    const keys: DiffKeyContext = {
      beforeModules: moduleNameIndex(beforeRaw.modules),
      afterModules: moduleNameIndex(afterRaw.modules),
      beforeCallables: callableIndex(beforeRaw),
      afterCallables: callableIndex(afterRaw),
    };
    // A fully added/removed process diffs against the empty scaffold — the
    // matcher tags every node, and geometry still comes from ONE layout.
    const union = diffScaffolds(b ?? EMPTY_SCAFFOLD, a ?? EMPTY_SCAFFOLD, keys);
    return bpmnLayout(union);
  }, [effectiveId, beforeRaw, afterRaw]);

  if (changed.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 text-sm" data-testid="process-diff-empty">
        No process changes.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0" data-testid="process-diff">
      <aside className="w-[240px] shrink-0 overflow-y-auto border-r border-slate-800 bg-slate-900/(--tint-strong)">
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
          Changed processes
        </div>
        <ul>
          {changed.map((p: ProcessDiff) => (
            <li key={p.id}>
              <button
                type="button"
                data-process-pick={p.name}
                onClick={() => setSelectedId(p.id)}
                onContextMenu={(e) =>
                  menu.open(e, [
                    { label: "Select", onClick: () => setSelectedId(p.id) },
                    { label: "Copy name", onClick: () => void navigator.clipboard?.writeText(p.name) },
                  ] satisfies ContextMenuItem[])
                }
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
                  effectiveId === p.id ? "bg-sky-400/[0.12] text-white" : "text-slate-300 hover:bg-slate-400/[0.06]"
                }`}
              >
                <span className="truncate flex-1">{p.name}</span>
                <span
                  className={`px-1.5 py-px rounded-full border text-[10px] ${STATUS_CHIP[p.status] ?? "border-slate-600 text-slate-400"}`}
                >
                  {p.status}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <div
          className="flex items-center gap-4 px-4 py-1.5 border-b border-slate-800 text-[11px] text-slate-400"
          data-testid="process-diff-legend"
        >
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm border-2 border-emerald-400 inline-block" /> added
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm border-2 border-dashed border-rose-400 inline-block" /> removed
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm border-2 border-amber-400 inline-block" /> changed
            <span className="text-slate-500">(detail chip below the node)</span>
          </span>
          {deltaLabel && (
            <span
              className="ml-auto rounded border border-slate-700 bg-slate-800/(--tint-strong) px-2 py-0.5 font-medium text-slate-300 tabular-nums"
              data-testid="process-diff-latency"
              title="Estimated worst-case process time — base → head (PROCESS.md §13)"
            >
              {deltaLabel}
            </span>
          )}
          <span className={`${deltaLabel ? "" : "ml-auto "}text-slate-500 italic`}>dimmed = unselected alternative</span>
        </div>
        <div className="flex-1 min-h-0">
          {layout ? (
            <BpmnView
              layout={layout}
              activeStepIndex={null}
              onSelectStepIndex={() => {}}
              onSubprocessToggle={() => {}}
              onSelectModule={() => {}}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-slate-500 text-sm">
              Pick a process.
            </div>
          )}
        </div>
      </div>
      {menu.node}
    </div>
  );
}
